import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import type {
  Warehouse,
  WarehouseZone,
  WarehouseRack,
  WarehouseBin,
  WarehouseLayout,
  WarehouseCreateData,
  WarehouseUpdateData,
  ZoneCreateData,
  ZoneUpdateData,
  RackCreateData,
  RackUpdateData,
  WarehouseWithRelations,
  WarehouseStatistics,
  ViewportState,
} from '../types/warehouse';
import {
  formatZoneCode,
  formatRackCode,
  getNextAvailableZoneNumber,
  getNextAvailableRackNumber,
  getNextAvailableZoneColor,
  generateBinsForRack,
  calculateWarehouseStatistics,
} from '../utils/warehouseUtils';

interface UseWarehousesReturn {
  warehouses: Warehouse[];
  loading: boolean;
  error: string | null;
  fetchWarehouses: () => Promise<void>;
  createWarehouse: (data: WarehouseCreateData) => Promise<Warehouse | null>;
  updateWarehouse: (id: string, data: WarehouseUpdateData) => Promise<Warehouse | null>;
  archiveWarehouse: (id: string) => Promise<boolean>;
  restoreWarehouse: (id: string) => Promise<boolean>;
  deleteWarehouse: (id: string) => Promise<boolean>;
}

export function useWarehouses(): UseWarehousesReturn {
  const { user } = useAuth();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const adminId = getAdminId(user);

  const fetchWarehouses = useCallback(async () => {
    if (!adminId) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('warehouses')
      .select('*')
      .eq('admin_id', adminId)
      .eq('is_archived', false)
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
      setWarehouses([]);
    } else {
      setWarehouses(data ?? []);
    }

    setLoading(false);
  }, [adminId]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const createWarehouse = useCallback(async (data: WarehouseCreateData): Promise<Warehouse | null> => {
    if (!adminId) return null;

    const { data: warehouse, error: createError } = await supabase
      .from('warehouses')
      .insert({
        admin_id: adminId,
        name: data.name.trim(),
        unit: data.unit,
        length: data.length,
        width: data.width,
        height: data.height ?? null,
      })
      .select()
      .single();

    if (createError) {
      setError(createError.message);
      return null;
    }

    if (warehouse) {
      await supabase.from('warehouse_layouts').insert({
        warehouse_id: warehouse.id,
        grid_size: 10,
        viewport_json: { x: 0, y: 0, scale: 1 },
      });

      setWarehouses(prev => [warehouse, ...prev]);
    }

    return warehouse;
  }, [adminId]);

  const updateWarehouse = useCallback(async (
    id: string,
    data: WarehouseUpdateData
  ): Promise<Warehouse | null> => {
    const updateData: Record<string, unknown> = {};

    if (data.name !== undefined) updateData.name = data.name.trim();
    if (data.unit !== undefined) updateData.unit = data.unit;
    if (data.length !== undefined) updateData.length = data.length;
    if (data.width !== undefined) updateData.width = data.width;
    if (data.height !== undefined) updateData.height = data.height;
    if (data.border_color !== undefined) updateData.border_color = data.border_color;
    if (data.map_unit !== undefined) updateData.map_unit = data.map_unit;
    if (data.grid_unit !== undefined) updateData.grid_unit = data.grid_unit;

    const { data: warehouse, error: updateError } = await supabase
      .from('warehouses')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      setError(updateError.message);
      return null;
    }

    if (warehouse) {
      setWarehouses(prev => prev.map(w => w.id === id ? warehouse : w));
    }

    return warehouse;
  }, []);

  const archiveWarehouse = useCallback(async (id: string): Promise<boolean> => {
    if (!user?.id) return false;

    const { error: archiveError } = await supabase
      .from('warehouses')
      .update({
        is_archived: true,
        archived_at: new Date().toISOString(),
        archived_by: user.id, // User performing the archive (can be worker)
      })
      .eq('id', id);

    if (archiveError) {
      setError(archiveError.message);
      return false;
    }

    setWarehouses(prev => prev.filter(w => w.id !== id));
    return true;
  }, []);

  const restoreWarehouse = useCallback(async (id: string): Promise<boolean> => {
    const { error: restoreError } = await supabase
      .from('warehouses')
      .update({
        is_archived: false,
        archived_at: null,
        archived_by: null,
      })
      .eq('id', id);

    if (restoreError) {
      setError(restoreError.message);
      return false;
    }

    await fetchWarehouses();
    return true;
  }, [fetchWarehouses]);

  const deleteWarehouse = useCallback(async (id: string): Promise<boolean> => {
    const { error: deleteError } = await supabase
      .from('warehouses')
      .delete()
      .eq('id', id);

    if (deleteError) {
      setError(deleteError.message);
      return false;
    }

    setWarehouses(prev => prev.filter(w => w.id !== id));
    return true;
  }, []);

  return {
    warehouses,
    loading,
    error,
    fetchWarehouses,
    createWarehouse,
    updateWarehouse,
    archiveWarehouse,
    restoreWarehouse,
    deleteWarehouse,
  };
}

interface UseWarehouseDetailReturn {
  warehouse: WarehouseWithRelations | null;
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  bins: WarehouseBin[];
  layout: WarehouseLayout | null;
  statistics: WarehouseStatistics;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateWarehouse: (data: WarehouseUpdateData) => Promise<boolean>;
  deleteWarehouse: () => Promise<boolean>;
  createZone: (data: Omit<ZoneCreateData, 'warehouse_id'>) => Promise<WarehouseZone | null>;
  updateZone: (id: string, data: ZoneUpdateData) => Promise<WarehouseZone | null>;
  deleteZone: (id: string) => Promise<boolean>;
  createRack: (data: Omit<RackCreateData, 'warehouse_id'>) => Promise<WarehouseRack | null>;
  updateRack: (id: string, data: RackUpdateData) => Promise<WarehouseRack | null>;
  deleteRack: (id: string) => Promise<boolean>;
  duplicateRacks: (sourceRack: WarehouseRack, count: number) => Promise<WarehouseRack[]>;
  updateLayout: (data: Partial<{ grid_size: number; viewport_json: ViewportState }>) => Promise<boolean>;
  bulkUpdateRackZones: (assignments: Map<string, string | null>) => Promise<boolean>;
}

export function useWarehouseDetail(warehouseId: string | null): UseWarehouseDetailReturn {
  const { user } = useAuth();
  const adminId = getAdminId(user);
  const [warehouse, setWarehouse] = useState<WarehouseWithRelations | null>(null);
  const [zones, setZones] = useState<WarehouseZone[]>([]);
  const [racks, setRacks] = useState<WarehouseRack[]>([]);
  const [bins, setBins] = useState<WarehouseBin[]>([]);
  const [layout, setLayout] = useState<WarehouseLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const statistics = calculateWarehouseStatistics(zones, racks, bins);

  const refresh = useCallback(async () => {
    if (!warehouseId || !adminId) {
      setWarehouse(null);
      setZones([]);
      setRacks([]);
      setBins([]);
      setLayout(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const [
      warehouseResult,
      zonesResult,
      racksResult,
      binsResult,
      layoutResult,
    ] = await Promise.all([
      supabase
        .from('warehouses')
        .select('*')
        .eq('id', warehouseId)
        .eq('admin_id', adminId)
        .maybeSingle(),
      supabase
        .from('warehouse_zones')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .order('zone_number', { ascending: true }),
      supabase
        .from('warehouse_racks')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .order('rack_number', { ascending: true }),
      supabase
        .from('warehouse_bins')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .order('sort_order', { ascending: true }),
      supabase
        .from('warehouse_layouts')
        .select('*')
        .eq('warehouse_id', warehouseId)
        .maybeSingle(),
    ]);

    if (warehouseResult.error) {
      setError(warehouseResult.error.message);
      setLoading(false);
      return;
    }

    setWarehouse(warehouseResult.data);
    setZones(zonesResult.data ?? []);
    setRacks(racksResult.data ?? []);
    setBins(binsResult.data ?? []);
    setLayout(layoutResult.data);
    setLoading(false);
  }, [warehouseId, adminId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateWarehouseDetail = useCallback(async (data: WarehouseUpdateData): Promise<boolean> => {
    if (!warehouseId) return false;

    const updateData: Record<string, unknown> = {};

    if (data.name !== undefined) updateData.name = data.name.trim();
    if (data.unit !== undefined) updateData.unit = data.unit;
    if (data.length !== undefined) updateData.length = data.length;
    if (data.width !== undefined) updateData.width = data.width;
    if (data.height !== undefined) updateData.height = data.height;
    if (data.border_color !== undefined) updateData.border_color = data.border_color;
    if (data.map_unit !== undefined) updateData.map_unit = data.map_unit;
    if (data.grid_unit !== undefined) updateData.grid_unit = data.grid_unit;

    const { data: updatedWarehouse, error: updateError } = await supabase
      .from('warehouses')
      .update(updateData)
      .eq('id', warehouseId)
      .select()
      .single();

    if (updateError) {
      setError(updateError.message);
      return false;
    }

    if (updatedWarehouse) {
      setWarehouse(updatedWarehouse);
    }

    return true;
  }, [warehouseId]);

  const deleteWarehouseDetail = useCallback(async (): Promise<boolean> => {
    if (!warehouseId) return false;

    const { error: deleteError } = await supabase
      .from('warehouses')
      .delete()
      .eq('id', warehouseId);

    if (deleteError) {
      setError(deleteError.message);
      return false;
    }

    setWarehouse(null);
    return true;
  }, [warehouseId]);

  const createZone = useCallback(async (
    data: Omit<ZoneCreateData, 'warehouse_id'>
  ): Promise<WarehouseZone | null> => {
    if (!warehouseId || !adminId) return null;

    const zoneNumber = getNextAvailableZoneNumber(zones);
    const color = data.color || getNextAvailableZoneColor(zones);

    const { data: zone, error: createError } = await supabase
      .from('warehouse_zones')
      .insert({
        warehouse_id: warehouseId,
        admin_id: adminId,
        zone_number: zoneNumber,
        zone_code: formatZoneCode(zoneNumber),
        zone_name: data.zone_name ?? '',
        color,
        x_position: data.x_position ?? 0,
        y_position: data.y_position ?? 0,
        zone_width: data.zone_width ?? 200,
        zone_height: data.zone_height ?? 200,
      })
      .select()
      .single();

    if (createError) {
      setError(createError.message);
      return null;
    }

    if (zone) {
      setZones(prev => [...prev, zone].sort((a, b) => a.zone_number - b.zone_number));
    }

    return zone;
  }, [warehouseId, adminId, zones]);

  const updateZone = useCallback(async (
    id: string,
    data: ZoneUpdateData
  ): Promise<WarehouseZone | null> => {
    const updateData: Record<string, unknown> = {};

    if (data.zone_name !== undefined) updateData.zone_name = data.zone_name;
    if (data.color !== undefined) updateData.color = data.color;
    if (data.x_position !== undefined) updateData.x_position = data.x_position;
    if (data.y_position !== undefined) updateData.y_position = data.y_position;
    if (data.zone_width !== undefined) updateData.zone_width = data.zone_width;
    if (data.zone_height !== undefined) updateData.zone_height = data.zone_height;

    const { data: zone, error: updateError } = await supabase
      .from('warehouse_zones')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      setError(updateError.message);
      return null;
    }

    if (zone) {
      setZones(prev => prev.map(z => z.id === id ? zone : z));
    }

    return zone;
  }, []);

  const deleteZone = useCallback(async (id: string): Promise<boolean> => {
    const { error: deleteError } = await supabase
      .from('warehouse_zones')
      .delete()
      .eq('id', id);

    if (deleteError) {
      setError(deleteError.message);
      return false;
    }

    setZones(prev => prev.filter(z => z.id !== id));
    setRacks(prev => prev.map(r => r.zone_id === id ? { ...r, zone_id: null } : r));
    return true;
  }, []);

  const createRack = useCallback(async (
    data: Omit<RackCreateData, 'warehouse_id'>
  ): Promise<WarehouseRack | null> => {
    if (!warehouseId || !adminId) return null;

    const rackNumber = getNextAvailableRackNumber(racks);
    const rackCode = formatRackCode(rackNumber);
    const levels = data.levels ?? 1;
    const bays = data.bays ?? 1;

    const { data: rack, error: createError } = await supabase
      .from('warehouse_racks')
      .insert({
        warehouse_id: warehouseId,
        admin_id: adminId,
        zone_id: data.zone_id ?? null,
        rack_number: rackNumber,
        rack_code: rackCode,
        rack_length: data.rack_length ?? 100,
        rack_depth: data.rack_depth ?? 50,
        rotation: data.rotation ?? 0,
        levels,
        bays,
        x_position: data.x_position ?? 0,
        y_position: data.y_position ?? 0,
        zone_manual_override: data.zone_manual_override ?? false,
      })
      .select()
      .single();

    if (createError) {
      setError(createError.message);
      return null;
    }

    if (rack) {
      const newBins = generateBinsForRack(rack.id, warehouseId, adminId, rackCode, levels, bays);

      if (newBins.length > 0) {
        const { data: insertedBins } = await supabase
          .from('warehouse_bins')
          .insert(newBins)
          .select();

        if (insertedBins) {
          setBins(prev => [...prev, ...insertedBins]);
        }
      }

      setRacks(prev => [...prev, rack].sort((a, b) => a.rack_number - b.rack_number));
    }

    return rack;
  }, [warehouseId, adminId, racks]);

  const updateRack = useCallback(async (
    id: string,
    data: RackUpdateData
  ): Promise<WarehouseRack | null> => {
    const existingRack = racks.find(r => r.id === id);
    if (!existingRack || !adminId || !warehouseId) return null;

    const updateData: Record<string, unknown> = {};

    if (data.zone_id !== undefined) updateData.zone_id = data.zone_id;
    if (data.rack_length !== undefined) updateData.rack_length = data.rack_length;
    if (data.rack_depth !== undefined) updateData.rack_depth = data.rack_depth;
    if (data.rotation !== undefined) updateData.rotation = data.rotation;
    if (data.levels !== undefined) updateData.levels = data.levels;
    if (data.bays !== undefined) updateData.bays = data.bays;
    if (data.x_position !== undefined) updateData.x_position = data.x_position;
    if (data.y_position !== undefined) updateData.y_position = data.y_position;
    if (data.zone_manual_override !== undefined) updateData.zone_manual_override = data.zone_manual_override;

    const { data: rack, error: updateError } = await supabase
      .from('warehouse_racks')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      setError(updateError.message);
      return null;
    }

    const levelsChanged = data.levels !== undefined && data.levels !== existingRack.levels;
    const baysChanged = data.bays !== undefined && data.bays !== existingRack.bays;

    if (rack && (levelsChanged || baysChanged)) {
      await supabase.from('warehouse_bins').delete().eq('rack_id', id);

      const newLevels = data.levels ?? existingRack.levels;
      const newBays = data.bays ?? existingRack.bays;
      const newBins = generateBinsForRack(id, warehouseId, adminId, existingRack.rack_code, newLevels, newBays);

      if (newBins.length > 0) {
        const { data: insertedBins } = await supabase
          .from('warehouse_bins')
          .insert(newBins)
          .select();

        setBins(prev => [
          ...prev.filter(b => b.rack_id !== id),
          ...(insertedBins ?? []),
        ]);
      } else {
        setBins(prev => prev.filter(b => b.rack_id !== id));
      }
    }

    if (rack) {
      setRacks(prev => prev.map(r => r.id === id ? rack : r));
    }

    return rack;
  }, [racks, user?.id, warehouseId]);

  const deleteRack = useCallback(async (id: string): Promise<boolean> => {
    const { error: deleteError } = await supabase
      .from('warehouse_racks')
      .delete()
      .eq('id', id);

    if (deleteError) {
      setError(deleteError.message);
      return false;
    }

    setRacks(prev => prev.filter(r => r.id !== id));
    setBins(prev => prev.filter(b => b.rack_id !== id));
    return true;
  }, []);

  const duplicateRacks = useCallback(async (
    sourceRack: WarehouseRack,
    count: number
  ): Promise<WarehouseRack[]> => {
    if (!warehouseId || !adminId || count < 1 || count > 20) return [];

    const createdRacks: WarehouseRack[] = [];
    const createdBins: WarehouseBin[] = [];
    let currentRacks = [...racks];

    const spacing = 10;
    const rackWidth = sourceRack.rotation === 90 || sourceRack.rotation === 270
      ? sourceRack.rack_depth
      : sourceRack.rack_length;
    const rackHeight = sourceRack.rotation === 90 || sourceRack.rotation === 270
      ? sourceRack.rack_length
      : sourceRack.rack_depth;

    const warehouseWidth = warehouse?.length ?? 1000;
    const warehouseHeight = warehouse?.width ?? 1000;

    const maxPerRow = Math.max(1, Math.floor((warehouseWidth - sourceRack.x_position) / (rackWidth + spacing)));

    const getNextNameSuffix = (baseName: string): number => {
      const existingNames = currentRacks
        .filter(r => r.rack_name?.startsWith(baseName))
        .map(r => {
          const match = r.rack_name?.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`));
          return match ? parseInt(match[1], 10) : 0;
        });
      return existingNames.length > 0 ? Math.max(...existingNames) + 1 : 1;
    };

    let nameSuffix = sourceRack.rack_name ? getNextNameSuffix(sourceRack.rack_name) : 1;

    for (let i = 0; i < count; i++) {
      const rackNumber = getNextAvailableRackNumber(currentRacks);
      const rackCode = formatRackCode(rackNumber);

      const col = (i + 1) % maxPerRow;
      const row = Math.floor((i + 1) / maxPerRow);

      let newX = sourceRack.x_position + col * (rackWidth + spacing);
      let newY = sourceRack.y_position + row * (rackHeight + spacing);

      newX = Math.max(0, Math.min(newX, warehouseWidth - rackWidth));
      newY = Math.max(0, Math.min(newY, warehouseHeight - rackHeight));

      const newRackName = sourceRack.rack_name
        ? `${sourceRack.rack_name}-${nameSuffix++}`
        : undefined;

      const { data: newRack, error: createError } = await supabase
        .from('warehouse_racks')
        .insert({
          warehouse_id: warehouseId,
          admin_id: adminId,
          zone_id: sourceRack.zone_id,
          rack_number: rackNumber,
          rack_code: rackCode,
          rack_name: newRackName,
          rack_length: sourceRack.rack_length,
          rack_depth: sourceRack.rack_depth,
          rack_height: sourceRack.rack_height,
          rotation: sourceRack.rotation,
          levels: sourceRack.levels,
          bays: sourceRack.bays,
          x_position: newX,
          y_position: newY,
          zone_manual_override: sourceRack.zone_manual_override,
        })
        .select()
        .single();

      if (createError) {
        setError(createError.message);
        break;
      }

      if (newRack) {
        const newBins = generateBinsForRack(
          newRack.id,
          warehouseId,
          adminId,
          rackCode,
          sourceRack.levels,
          sourceRack.bays
        );

        if (newBins.length > 0) {
          const { data: insertedBins } = await supabase
            .from('warehouse_bins')
            .insert(newBins)
            .select();

          if (insertedBins) {
            createdBins.push(...insertedBins);
          }
        }

        createdRacks.push(newRack);
        currentRacks = [...currentRacks, newRack];
      }
    }

    if (createdRacks.length > 0) {
      setRacks(prev => [...prev, ...createdRacks].sort((a, b) => a.rack_number - b.rack_number));
      setBins(prev => [...prev, ...createdBins]);
    }

    return createdRacks;
  }, [warehouseId, adminId, racks, warehouse]);

  const updateLayout = useCallback(async (
    data: Partial<{ grid_size: number; viewport_json: ViewportState }>
  ): Promise<boolean> => {
    if (!warehouseId) return false;

    if (!layout) {
      const { data: newLayout, error: createError } = await supabase
        .from('warehouse_layouts')
        .insert({
          warehouse_id: warehouseId,
          grid_size: data.grid_size ?? 10,
          viewport_json: data.viewport_json ?? { x: 0, y: 0, scale: 1 },
        })
        .select()
        .single();

      if (createError) {
        setError(createError.message);
        return false;
      }

      setLayout(newLayout);
      return true;
    }

    const updateData: Record<string, unknown> = {};
    if (data.grid_size !== undefined) updateData.grid_size = data.grid_size;
    if (data.viewport_json !== undefined) updateData.viewport_json = data.viewport_json;

    const { data: updatedLayout, error: updateError } = await supabase
      .from('warehouse_layouts')
      .update(updateData)
      .eq('warehouse_id', warehouseId)
      .select()
      .single();

    if (updateError) {
      setError(updateError.message);
      return false;
    }

    setLayout(updatedLayout);
    return true;
  }, [warehouseId, layout]);

  const bulkUpdateRackZones = useCallback(async (
    assignments: Map<string, string | null>
  ): Promise<boolean> => {
    const updateEntries: Array<{ rackId: string; zoneId: string | null }> = [];

    for (const [rackId, zoneId] of assignments) {
      const rack = racks.find(r => r.id === rackId);
      if (!rack || rack.zone_id === zoneId) continue;
      updateEntries.push({ rackId, zoneId });
    }

    if (updateEntries.length === 0) return true;

    for (const entry of updateEntries) {
      await supabase
        .from('warehouse_racks')
        .update({ zone_id: entry.zoneId })
        .eq('id', entry.rackId);
    }

    setRacks(prev => prev.map(rack => {
      const newZoneId = assignments.get(rack.id);
      if (newZoneId === undefined || rack.zone_id === newZoneId) return rack;
      return { ...rack, zone_id: newZoneId };
    }));

    return true;
  }, [racks]);

  return {
    warehouse,
    zones,
    racks,
    bins,
    layout,
    statistics,
    loading,
    error,
    refresh,
    updateWarehouse: updateWarehouseDetail,
    deleteWarehouse: deleteWarehouseDetail,
    createZone,
    updateZone,
    deleteZone,
    createRack,
    updateRack,
    deleteRack,
    duplicateRacks,
    updateLayout,
    bulkUpdateRackZones,
  };
}
